import React, { useState, useEffect } from 'react';
import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Button, 
    TextField, 
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem, 
    CircularProgress,
    Box,
    Typography,
    InputAdornment,
    Grid,
    IconButton
} from '@mui/material';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteIcon from '@mui/icons-material/Delete';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import FileUploadDropzone from '../../../components/FileUploadDropzone';
import { FinanceService } from '../../../api/services/financeService';
import { SettingsService } from '../../../api/services/settingsService';
import { toast } from '../../../utils/toast';
import { useCurrency } from '../../../utils/currency';
import { useSelector } from 'react-redux';

const EXPENSE_CATEGORIES = [
    'Rent',
    'Salaries',
    'Utilities',
    'Petty Cash',
    'Stationery',
    'Cleaning Supplies',
    'Minor Repairs',
    'Vendor Payout',
    'Inventory & Supplies'
];

export default function LogExpenseModal({ open, onClose, onSuccess }) {
    const { currencySymbol } = useCurrency();
    const { user } = useSelector((state) => state.auth || {});
    const activeTenantId = user?.tenantId;

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [previewPopup, setPreviewPopup] = useState(false);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loadingMethods, setLoadingMethods] = useState(false);
    const [form, setForm] = useState({
        category: '',
        description: '',
        amount: '',
        date: '',
        paymentMethod: '',
        referenceNumber: '',
        receiptUrl: ''
    });

    const fetchPaymentMethods = async () => {
        setLoadingMethods(true);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const resolvedTenantId = activeTenantId || authData.tenantId || null;
            const res = await SettingsService.getPaymentMethods(resolvedTenantId, { noPagination: true });
            const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
            const activeList = list.filter(m => m.enabled === undefined || m.enabled === true || m.status === 'Active');
            setPaymentMethods(activeList);
        } catch (err) {
            console.error('Failed to load payment methods from API:', err);
            setPaymentMethods([]);
        } finally {
            setLoadingMethods(false);
        }
    };

    useEffect(() => {
        if (open) {
            // Set default date to current local date
            const now = new Date();
            // Format YYYY-MM-DD for native HTML5 date input
            const localISOTime = (new Date(now.getTime() - now.getTimezoneOffset() * 60000)).toISOString().slice(0,10);
            
            setForm({
                category: '',
                description: '',
                amount: '',
                date: localISOTime,
                paymentMethod: '',
                referenceNumber: '',
                receiptUrl: ''
            });

            fetchPaymentMethods();
        }
    }, [open]);

    const handleTextChange = (field) => (event) => {
        setForm(prev => ({ ...prev, [field]: event.target.value }));
    };

    const handleAmountChange = (e) => {
        const val = e.target.value;
        if (!val || val.match(/^\d*\.?\d*$/)) {
            setForm(prev => ({ ...prev, amount: val }));
        }
    };

    const handleSubmit = async () => {
        if (!form.category || !form.description.trim() || !form.amount || !form.date || !form.paymentMethod) {
            toast.error('Please fill out all required expense fields.');
            return;
        }

        const amountVal = parseFloat(form.amount);
        if (isNaN(amountVal) || amountVal <= 0) {
            toast.error('Expense amount must be a positive number greater than zero.');
            return;
        }

        setIsSubmitting(true);
        try {
            const now = new Date();
            const todayStr = (new Date(now.getTime() - now.getTimezoneOffset() * 60000)).toISOString().slice(0, 10);
            let dateISO;
            if (form.date === todayStr) {
                dateISO = now.toISOString();
            } else if (form.date) {
                const [year, month, day] = form.date.split('-').map(Number);
                const d = new Date(year, month - 1, day, 12, 0, 0);
                dateISO = !isNaN(d.getTime()) ? d.toISOString() : new Date(form.date).toISOString();
            } else {
                dateISO = now.toISOString();
            }

            const finalPaymentMethod = (form.paymentMethod === 'Cash / Petty Cash' || form.paymentMethod === 'Petty Cash')
                ? 'Cash'
                : form.paymentMethod;

            const payload = {
                category: form.category,
                amount: amountVal,
                description: form.description.trim(),
                date: dateISO,
                paymentMethod: finalPaymentMethod,
                ...(form.referenceNumber?.trim() ? { referenceNumber: form.referenceNumber.trim() } : {}),
                ...(form.receiptUrl ? { receiptUrl: form.receiptUrl } : {})
            };

            await FinanceService.logExpense(payload);
            toast.success('New operational expense successfully logged and audited');
            onSuccess();
            onClose();
        } catch (err) {
            console.error('Failed to log operational expense:', err);
            const errorMsg = 
                err?.response?.data?.message || 
                err?.message || 
                'Failed to log operational expense. Please check input values.';
            toast.error(errorMsg);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
        <Dialog 
            open={open} 
            onClose={!isSubmitting ? onClose : undefined} 
            maxWidth="sm" 
            fullWidth 
            PaperProps={{ sx: { borderRadius: 3 } }}
        >
            <DialogTitle 
                sx={{ 
                    borderBottom: '1px solid #eee', 
                    bgcolor: '#fbfbfb', 
                    color: 'primary.main', 
                    display: 'flex', 
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 2,
                    px: 3
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RequestQuoteIcon sx={{ color: 'primary.main' }} /> 
                    <Typography variant="h6" fontWeight="bold" component="span" sx={{ color: 'primary.main' }}>
                        Log Expense Record
                    </Typography>
                </Box>
                <IconButton 
                    aria-label="close"
                    onClick={onClose} 
                    disabled={isSubmitting}
                    size="small"
                    sx={{ color: 'grey.500', '&:hover': { color: 'text.primary', bgcolor: 'grey.100' } }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 3.5 }}>
                <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Register a distinct operational company or property cost directly into the master accounting ledger.
                    </Typography>

                    <Grid container spacing={2.5}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required>
                                <InputLabel id="log-expense-category-label">Expense Category</InputLabel>
                                <Select
                                    labelId="log-expense-category-label"
                                    value={form.category}
                                    label="Expense Category *"
                                    onChange={handleTextChange('category')}
                                    disabled={isSubmitting}
                                >
                                    {EXPENSE_CATEGORIES.map((cat) => (
                                        <MenuItem key={cat} value={cat}>
                                            {cat}
                                        </MenuItem>
                                    ))}
                                    {form.category && !EXPENSE_CATEGORIES.includes(form.category) && (
                                        <MenuItem key={form.category} value={form.category}>
                                            {form.category}
                                        </MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                required
                                label="Expense Amount"
                                placeholder="0.00"
                                value={form.amount}
                                onChange={handleAmountChange}
                                disabled={isSubmitting}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                required
                                label="Line-Item Description"
                                placeholder="e.g., Electricity Bill May 2026"
                                value={form.description}
                                onChange={handleTextChange('description')}
                                disabled={isSubmitting}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                required
                                label="Posting Date"
                                type="date"
                                value={form.date}
                                onChange={handleTextChange('date')}
                                disabled={isSubmitting}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required disabled={isSubmitting || loadingMethods}>
                                <InputLabel>Payment Method</InputLabel>
                                <Select
                                    value={form.paymentMethod}
                                    label="Payment Method"
                                    onChange={handleTextChange('paymentMethod')}
                                    disabled={isSubmitting || loadingMethods}
                                >
                                    {loadingMethods ? (
                                        <MenuItem disabled value="">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <CircularProgress size={16} />
                                                <Typography variant="body2">Loading payment methods...</Typography>
                                            </Box>
                                        </MenuItem>
                                    ) : paymentMethods.length > 0 ? (
                                        Array.from(new Map(paymentMethods.map(m => {
                                            const rawVal = typeof m === 'string' ? m : (m.name || m.code || m.id);
                                            const val = (rawVal === 'Cash / Petty Cash' || rawVal === 'Petty Cash') ? 'Cash' : rawVal;
                                            const rawLabel = typeof m === 'string' ? m : (m.label || m.name || m.code || val);
                                            const label = (rawLabel === 'Cash / Petty Cash' || rawLabel === 'Petty Cash') ? 'Cash' : rawLabel;
                                            return [val, { key: m.id || m._id || val, val, label }];
                                        })).values()).map(({ key, val, label }) => (
                                            <MenuItem key={key} value={val}>
                                                {label}
                                            </MenuItem>
                                        ))
                                    ) : (
                                        <MenuItem disabled value="">No payment methods available</MenuItem>
                                    )}
                                    {form.paymentMethod && form.paymentMethod !== 'Cash' && !paymentMethods.some(m => (typeof m === 'string' ? m : (m.name || m.code || m.id)) === form.paymentMethod) && (
                                        <MenuItem key={form.paymentMethod} value={form.paymentMethod}>
                                            {form.paymentMethod}
                                        </MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Transaction Reference Number"
                                placeholder="e.g., REF_ELEC_123, Invoice ID (Optional)"
                                value={form.referenceNumber}
                                onChange={handleTextChange('referenceNumber')}
                                disabled={isSubmitting}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2" fontWeight="medium" color="text.secondary" sx={{ mb: 1 }}>
                                Receipt / Invoice Attachment
                            </Typography>
                            {form.receiptUrl ? (
                                <Box 
                                    sx={{ 
                                        p: 2, 
                                        border: '1px solid', 
                                        borderColor: 'divider', 
                                        borderRadius: 2, 
                                        bgcolor: 'background.paper',
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'space-between',
                                        gap: 2
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
                                        <Box 
                                            sx={{ 
                                                width: 42, 
                                                height: 42, 
                                                borderRadius: 1.5, 
                                                bgcolor: 'primary.50', 
                                                color: 'primary.main',
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'center',
                                                flexShrink: 0
                                            }}
                                        >
                                            <InsertDriveFileIcon />
                                        </Box>
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography variant="subtitle2" noWrap fontWeight="bold">
                                                Receipt Uploaded
                                            </Typography>
                                            <Typography 
                                                variant="caption" 
                                                color="text.secondary" 
                                                noWrap 
                                                sx={{ display: 'block', maxWidth: 280 }}
                                            >
                                                {form.receiptUrl}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                        <Button 
                                            size="small" 
                                            variant="outlined" 
                                            color="primary"
                                            startIcon={<ReceiptLongIcon sx={{ fontSize: 14 }} />}
                                            onClick={() => setPreviewPopup(true)}
                                            disabled={isSubmitting}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            View
                                        </Button>
                                        <IconButton 
                                            size="small" 
                                            color="error" 
                                            disabled={isSubmitting}
                                            onClick={() => setForm(prev => ({ ...prev, receiptUrl: '' }))}
                                            title="Remove Receipt"
                                            sx={{ border: '1px solid', borderColor: 'error.light' }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Box>
                            ) : (
                                <FileUploadDropzone 
                                    accept="image/*,application/pdf"
                                    maxSizeMB={5}
                                    onUploadSuccess={(url) => {
                                        console.log(url,"UUUU")
                                        setForm(prev => ({ ...prev, receiptUrl: url }));
                                    }} 
                                />
                            )}
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2.5, borderTop: '1px solid #eee', bgcolor: '#fbfbfb' }}>
                <Button onClick={onClose} color="inherit" disabled={isSubmitting}>
                    Cancel
                </Button>
                <Button 
                    onClick={handleSubmit} 
                    variant="contained" 
                    color="primary"
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                    sx={{ px: 3 }}
                >
                    {isSubmitting ? 'Recording...' : 'Log Expense'}
                </Button>
            </DialogActions>
        </Dialog>

        {/* Receipt Preview Popup Dialog */}
        <Dialog 
            open={previewPopup} 
            onClose={() => setPreviewPopup(false)} 
            maxWidth="md" 
            fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                borderBottom: '1px solid #eee',
                bgcolor: '#fbfbfb',
                py: 1.5,
                px: 3
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ReceiptLongIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight="bold">
                        Uploaded Receipt Preview
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                   { console.log(form,"formm")}
                    {form.receiptUrl && (
                        <Button
                            size="small"
                            variant="text"
                            color="primary"
                            href={form.receiptUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<OpenInNewIcon fontSize="small" />}
                            sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                        >
                            Open in new tab
                        </Button>
                    )}
                    <IconButton onClick={() => setPreviewPopup(false)} size="small" sx={{ color: 'grey.500' }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2, minHeight: 300, bgcolor: '#fafafa' }}>
                {form.receiptUrl && (
                    form.receiptUrl.toLowerCase().includes('.pdf') ? (
                        <Box 
                            component="iframe" 
                            src={form.receiptUrl} 
                            title="Expense Receipt Document" 
                            sx={{ width: '100%', height: '75vh', border: 'none', borderRadius: 2 }} 
                        />
                    ) : (
                        <Box 
                            component="img" 
                            src={form.receiptUrl} 
                            alt="Expense Receipt" 
                            sx={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }} 
                        />
                    )
                )}
            </DialogContent>
        </Dialog>
        </>
    );
}
